import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ClipboardList,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Users,
  AlertTriangle,
  Plus,
  Calendar,
  Award,
  Target,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentQCs, setRecentQCs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewalStats, setRenewalStats] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  
  // Technician score state
  const [myScore, setMyScore] = useState(null);
  const [techStartDate, setTechStartDate] = useState(today);
  const [techEndDate, setTechEndDate] = useState(today);

  useEffect(() => {
    fetchData(true);
    fetchRenewalStats();
    // Auto-refresh every 15 seconds for real-time status updates
    const interval = setInterval(() => {
      fetchData(false);
      fetchRenewalStats();
    }, 15000);
    return () => clearInterval(interval);
  }, [startDate, endDate, techStartDate, techEndDate]);

  useEffect(() => {
    if (!isAdmin) {
      fetchMyScore();
      // Auto-refresh score every 15 seconds
      const interval = setInterval(fetchMyScore, 15000);
      return () => clearInterval(interval);
    }
  }, [techStartDate, techEndDate, isAdmin]);

  const fetchData = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      if (isAdmin) {
        const [statsRes, qcRes] = await Promise.all([
          api.get('/reports/dashboard', { params: { start_date: startDate, end_date: endDate } }),
          api.get('/qc'),
        ]);
        setStats(statsRes.data);
        // Filter recent QCs by the selected date range
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        const filtered = qcRes.data.filter((qc) => {
          const qcDate = new Date(qc.created_timestamp);
          return qcDate >= start && qcDate <= end;
        });
        setRecentQCs(filtered.slice(0, 5));
      } else {
        const qcRes = await api.get('/qc');
        // Filter recent QCs by the technician's selected date range
        const start = new Date(techStartDate || today);
        start.setHours(0, 0, 0, 0);
        const end = new Date(techEndDate || today);
        end.setHours(23, 59, 59, 999);
        const filtered = qcRes.data.filter((qc) => {
          const qcDate = new Date(qc.created_timestamp);
          return qcDate >= start && qcDate <= end;
        });
        setRecentQCs(filtered.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (showLoader) toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyScore = async () => {
    try {
      const response = await api.get('/my-score', {
        params: { start_date: techStartDate, end_date: techEndDate }
      });
      setMyScore(response.data);
    } catch (error) {
      console.error('Failed to fetch my score:', error);
    }
  };

  const fetchRenewalStats = async () => {
    try {
      const response = await api.get('/renewals/dashboard-stats');
      setRenewalStats(response.data);
    } catch (error) {
      console.error('Failed to fetch renewal stats:', error);
    }
  };

  const formatDateRange = () => {
    const start = new Date(startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const end = new Date(endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (startDate === endDate) {
      return start;
    }
    return `${start} - ${end}`;
  };

  const formatTechDateRange = () => {
    const start = new Date(techStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const end = new Date(techEndDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (techStartDate === techEndDate) {
      return start;
    }
    return `${start} - ${end}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, trend, onClick }) => (
    <div
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className={`bg-white rounded-xl border border-border shadow-sm p-6 relative overflow-hidden transition-all ${
        onClick ? 'cursor-pointer hover:shadow-lg hover:border-primary/50 hover:scale-[1.02]' : 'hover:shadow-md'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
            {title}
          </p>
          <p className="text-4xl font-bold mb-1">{value}</p>
          {trend && (
            <p className="text-sm text-muted-foreground">{trend}</p>
          )}
        </div>
        <div className={`w-14 h-14 rounded-lg bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-7 h-7 text-${color}-600`} />
        </div>
      </div>
      {onClick && (
        <div className="absolute bottom-2 right-3 text-xs text-primary font-medium">
          Click to view →
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
            Welcome, {user?.full_name}
          </h1>
          <p className="text-lg text-muted-foreground">
            {isAdmin ? 'Admin Dashboard' : 'Technician Dashboard'}
          </p>
        </div>
        <Button
          onClick={() => navigate('/qc/new')}
          data-testid="new-qc-button"
          size="lg"
          className="h-14 px-8 font-bold uppercase tracking-wide shadow-lg shadow-primary/20"
        >
          <Plus className="w-5 h-5 mr-2" />
          New QC Check
        </Button>
      </div>

      {/* Date Range Filter for Stats (Admin Only) */}
      {isAdmin && (
        <div className="bg-white rounded-xl border border-border shadow-sm p-4">
          <div className="flex flex-col md:flex-row items-end gap-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <Label className="font-medium whitespace-nowrap">Date Range:</Label>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-10"
                  data-testid="dashboard-start-date"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-10"
                  data-testid="dashboard-end-date"
                />
              </div>
            </div>
            <p className="text-sm text-muted-foreground whitespace-nowrap">
              Showing: <span className="font-bold">{formatDateRange()}</span>
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid (Admin Only) */}
      {isAdmin && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total QC Checks"
            value={stats.total_qc_checks}
            icon={ClipboardList}
            color="blue"
            onClick={() => navigate(`/qc/list?start_date=${startDate}&end_date=${endDate}`)}
          />
          <StatCard
            title="Passed"
            value={stats.pass_count}
            icon={CheckCircle}
            color="green"
            trend={stats.total_qc_checks > 0 ? `${Math.round((stats.pass_count / stats.total_qc_checks) * 100)}% pass rate` : '0% pass rate'}
            onClick={() => navigate(`/drill-down?type=pass&start_date=${startDate}&end_date=${endDate}`)}
          />
          <StatCard
            title="On Hold"
            value={stats.hold_count}
            icon={XCircle}
            color="red"
            onClick={() => navigate(`/drill-down?type=hold&start_date=${startDate}&end_date=${endDate}`)}
          />
          <StatCard
            title="Second Check Pending"
            value={stats.second_check_pending}
            icon={Clock}
            color="yellow"
            onClick={() => navigate(`/drill-down?type=second-check&start_date=${startDate}&end_date=${endDate}`)}
          />
        </div>
      )}

      {/* Renewal Dashboard (Admin Only) */}
      {isAdmin && renewalStats && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center">
              <RefreshCw className="w-6 h-6 mr-2 text-primary" />
              Renewals
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Overdue"
              value={renewalStats.overdue_count}
              icon={XCircle}
              color="red"
              onClick={() => navigate('/renewals?filter=overdue')}
            />
            <StatCard
              title="Due Today"
              value={renewalStats.due_today_count}
              icon={Clock}
              color="yellow"
              onClick={() => navigate('/renewals?filter=due_today')}
            />
            <StatCard
              title="Upcoming (7 Days)"
              value={renewalStats.upcoming_count}
              icon={Calendar}
              color="blue"
              onClick={() => navigate('/renewals?filter=upcoming')}
            />
          </div>
        </div>
      )}

      {/* Technician Performance Score (Non-Admin) */}
      {!isAdmin && (
        <div className="space-y-4">
          {/* Date Range Filter for Technician */}
          <div className="bg-white rounded-xl border border-border shadow-sm p-4">
            <div className="flex flex-col md:flex-row items-end gap-4">
              <div className="flex items-center space-x-2">
                <Award className="w-5 h-5 text-primary" />
                <Label className="font-medium whitespace-nowrap">My Performance</Label>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Input
                    type="date"
                    value={techStartDate}
                    onChange={(e) => setTechStartDate(e.target.value)}
                    className="h-10"
                    data-testid="tech-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">End Date</Label>
                  <Input
                    type="date"
                    value={techEndDate}
                    onChange={(e) => setTechEndDate(e.target.value)}
                    className="h-10"
                    data-testid="tech-end-date"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground whitespace-nowrap">
                Showing: <span className="font-bold">{formatTechDateRange()}</span>
              </p>
            </div>
          </div>

          {/* Technician Score Card */}
          {myScore && (
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-white rounded-xl border border-primary/20 shadow-lg p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                {/* Total Points - Hero */}
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                    <Target className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider">Total Points</p>
                    <p className="text-5xl font-bold text-primary">{myScore.total_points}</p>
                  </div>
                </div>

                {/* Divider */}
                <div className="hidden md:block w-px h-20 bg-border"></div>

                {/* Points Breakdown */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{myScore.new_qc_count}</p>
                    <p className="text-xs text-muted-foreground">New QCs</p>
                    <p className="text-sm font-medium text-blue-600">+{myScore.qc_points} pts</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{myScore.second_checks_done}</p>
                    <p className="text-xs text-muted-foreground">2nd Checks</p>
                    <p className="text-sm font-medium text-green-600">+{myScore.second_check_points} pts</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{myScore.pass_count}</p>
                    <p className="text-xs text-muted-foreground">Passed</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{myScore.hold_count}</p>
                    <p className="text-xs text-muted-foreground">On Hold</p>
                  </div>
                </div>
              </div>

              {/* Scoring Legend */}
              <div className="mt-4 pt-4 border-t border-primary/10 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center">
                  <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                  70 pts per New QC
                </span>
                <span className="flex items-center">
                  <span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                  30 pts per Second Check
                </span>
              </div>
            </div>
          )}

          {myScore && myScore.total_points === 0 && (
            <div className="bg-muted/50 rounded-xl p-8 text-center">
              <Target className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-lg font-medium text-muted-foreground">No activity for selected date range</p>
              <p className="text-sm text-muted-foreground">Complete QC checks to earn points!</p>
            </div>
          )}
        </div>
      )}

      {/* Top Hold Reasons (Admin Only) */}
      {isAdmin && stats && stats.top_hold_reasons.length > 0 && (
        <div className="bg-white rounded-xl border border-border shadow-sm p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-6 h-6 text-destructive mr-3" />
            <h2 className="text-2xl font-bold">Top Hold Reasons</h2>
          </div>
          <div className="space-y-3">
            {stats.top_hold_reasons.map((reason, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="font-medium">{reason.reason}</span>
                <span className="px-3 py-1 bg-destructive/10 text-destructive rounded-full text-sm font-bold">
                  {reason.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Technician Productivity (Admin Only) */}
      {isAdmin && stats && stats.technician_productivity.length > 0 && (
        <div className="bg-white rounded-xl border border-border shadow-sm p-6">
          <div className="flex items-center mb-4">
            <Users className="w-6 h-6 text-primary mr-3" />
            <h2 className="text-2xl font-bold">Technician Productivity</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.technician_productivity.map((tech, idx) => (
              <div key={idx} className="p-4 bg-muted rounded-lg">
                <p className="font-bold text-lg mb-2">{tech.technician}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-bold">{tech.total_checks}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-600">Pass:</span>
                  <span className="font-bold text-green-600">{tech.pass_count}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-red-600">Hold:</span>
                  <span className="font-bold text-red-600">{tech.hold_count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent QC Entries */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Recent QC Entries</h2>
          <Button
            variant="outline"
            onClick={() => navigate('/qc/list')}
            data-testid="view-all-qcs-button"
          >
            View All
          </Button>
        </div>
        {recentQCs.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No QC entries yet</p>
        ) : (
          <div className="space-y-3">
            {recentQCs.map((qc) => (
              <div
                key={qc.id}
                data-testid={`qc-entry-${qc.id}`}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-border rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => navigate(`/qc/${qc.id}`)}
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="font-bold text-lg">{qc.asset_id}</span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${
                        qc.qc_status === 'PASS'
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : qc.qc_status === 'HOLD'
                          ? 'bg-red-100 text-red-700 border-red-200'
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}
                    >
                      {qc.qc_status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {qc.product_category} • {qc.customer_or_company_name} • {qc.qc_event_type}
                  </p>
                </div>
                <div className="mt-3 md:mt-0 text-right">
                  <p className="text-sm font-medium">{qc.created_by_technician}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(qc.created_timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Button
          onClick={() => navigate('/assets')}
          data-testid="quick-action-assets"
          variant="outline"
          className="h-20 text-lg font-semibold"
        >
          Manage Assets
        </Button>
        <Button
          onClick={() => navigate('/second-check')}
          data-testid="quick-action-second-check"
          variant="outline"
          className="h-20 text-lg font-semibold"
        >
          Second Check Queue
        </Button>
        <Button
          onClick={() => navigate('/history')}
          data-testid="quick-action-history"
          variant="outline"
          className="h-20 text-lg font-semibold"
        >
          View History
        </Button>
      </div>
    </div>
  );
};

export default DashboardPage;